import * as React from 'react';

import type { ContentRefURL } from '@gitbook/api-client';
import type { BlockDrawingData } from '@gitbook/doc-core';
import { ModalAnimateLifecycle, useDoublePress } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { LoadingIndicator, toast } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useClearContentReferenceCache } from '../../ContentReferences';
import { useUploadFiles } from '../../Files';
import { useInsertFiles } from '../../InsertFilesDialog';
import { EditableCaption, useCaption } from '../caption';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';
import { ContentEditorContext } from '../contexts';
import { DrawingEditModal } from './DrawingEditModal';
import { DrawingStaticView, type DrawingStaticViewRef } from './DrawingStaticView';

/**
 * Renderer for a drawing block.
 */
export function BlockDrawing(props: BlockProps) {
    const { node, editor, isFocused } = props;
    const { readOnly } = editor;

    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const clearContentReferenceCache = useClearContentReferenceCache();

    const blockData = node.data.toFrozenObject() as BlockDrawingData;
    const isEmpty = !blockData.ref;

    const showToastTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const insertFiles = useInsertFiles();
    const { uploadAndUpdateFile, uploadAndInsertFiles } = useUploadFiles();
    const [modal, setModal] = React.useState<'edit' | null>(null);
    const [blobRef, setBlobRef] = React.useState<ContentRefURL | null>(null);
    const [saving, wrapSaving] = useLoadingState();
    const drawingStaticRef = React.useRef<DrawingStaticViewRef>(null);
    const dismissToastRef = React.useRef<null | (() => void)>(null);

    const onStartEdit = () => {
        dismissToastRef.current?.();
        setModal('edit');

        logAnalyticsEvent({
            type: 'edit_drawing_edit',
            existing: !isEmpty,
        });
    };

    React.useEffect(() => {
        return () => {
            if (showToastTimeoutRef.current) {
                clearTimeout(showToastTimeoutRef.current);
            }
            dismissToastRef.current?.();
        };
    }, []);

    //
    // Double-pressing the block to edit
    // Single pressing shows a tooltip to help the user understands they need to double click
    //
    const onDoublePressEdit = useDoublePress(onStartEdit);
    const onPress = () => {
        if (readOnly) {
            drawingStaticRef.current?.openInGallery();
        } else {
            if (showToastTimeoutRef.current) {
                clearTimeout(showToastTimeoutRef.current);
            }
            dismissToastRef.current?.();

            if (!onDoublePressEdit()) {
                showToastTimeoutRef.current = setTimeout(() => {
                    const id = toast('Double click on the drawing to edit it', {
                        icon: Icon.EditDrawing,
                    });
                    dismissToastRef.current = () => toast.dismiss(id);
                }, 500);

                if (!isFocused) {
                    editor.change((change) => {
                        change.collapseToStartOf(node);
                        change.focus();
                    });
                }
            }
        }
    };

    const onSave = async (file: File) => {
        setBlobRef({ kind: 'url', url: URL.createObjectURL(file) });
        await wrapSaving(async () => {
            if (blockData.ref) {
                await uploadAndUpdateFile(blockData.ref.file, file);
                clearContentReferenceCache(blockData.ref);
                return;
            }

            const [fileRef] = await uploadAndInsertFiles([file]);
            editor.change((change) => {
                change.setDataByKey(node.key, ['ref'], fileRef);
            });
        });
    };

    const onCloseEdit = async (file: File | undefined) => {
        setModal(null);
        setBlobRef(null);

        if (file) {
            await onSave(file);
        }
    };

    const caption = useCaption(node, editor);

    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () => [
            {
                key: 'types',
                commands: [
                    {
                        key: 'edit',
                        title: 'Edit drawing',
                        icon: Icon.EditDrawing,
                        trigger: () => {
                            onStartEdit();
                        },
                    },
                    {
                        key: 'files',
                        title: 'Replace file…',
                        icon: Icon.Replace,
                        trigger: async () => {
                            const [ref] = await insertFiles.prompt({
                                title: 'Select drawing',
                                acceptURL: false,
                                accept: 'drawings',
                            });

                            if (ref?.kind !== 'file') {
                                return;
                            }

                            editor.change((change) => {
                                change.insertDataByKey(node.key, ['ref'], ref);
                            });
                        },
                    },
                    {
                        key: 'caption',
                        title: caption.hasCaption ? 'Edit caption' : 'Add caption',
                        icon: Icon.Caption,
                        trigger: () => {
                            caption.selectCaption();
                        },
                    },
                    caption.hasCaption
                        ? {
                              key: 'caption-remove',
                              title: 'Remove caption',
                              icon: Icon.CaptionRemove,
                              trigger: () => {
                                  caption.removeCaption();
                              },
                          }
                        : null,
                ].filter(filterOutNullable),
            },
        ],
    });

    return (
        <>
            <BlockWrapper {...props} palette={palette}>
                <VoidBlockFrame {...props} isFocused={isFocused}>
                    <div className="flex cursor-pointer flex-col" onClick={onPress}>
                        <React.Suspense fallback={<BlockLoadingFallback />}>
                            <DrawingStaticView
                                ref={drawingStaticRef}
                                nodeKey={node.key}
                                readOnly={readOnly}
                                contentRef={
                                    // Use blob ref while saving, it's optimistic
                                    blobRef && saving ? blobRef : (blockData.ref ?? null)
                                }
                                // Use the blob ref as a fallback while loading the drawing
                                fallbackContentRef={blobRef}
                                onSave={onCloseEdit}
                            />
                        </React.Suspense>
                        {saving ? (
                            <div className="absolute top-0 right-0 m-2 items-center justify-center rounded bg-base p-2">
                                <LoadingIndicator />
                            </div>
                        ) : null}
                    </div>
                </VoidBlockFrame>
                <EditableCaption {...props} />
            </BlockWrapper>
            <ModalAnimateLifecycle>
                {modal === 'edit' ? (
                    <DrawingEditModal
                        nodeKey={node.key}
                        blockData={blockData}
                        onClose={onCloseEdit}
                    />
                ) : null}
            </ModalAnimateLifecycle>
        </>
    );
}
